/**
 * RecordingControls - Audio recording interface
 */
import React, { memo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Crown, ChevronDown } from 'lucide-react';
import useCollapsePref from '../hooks/useCollapsePref';

const RecordingControls = memo(({
  isRecording,
  isPremium,
  isPlaying,
  filename,
  formattedDuration,
  exportFormat = 'mp3',
  onFilenameChange,
  onExportFormatChange,
  onStartRecording,
  onStopRecording,
  onDownload,
}) => {
  const [open, toggleOpen] = useCollapsePref('record_session', false);
  return (
    <div className="space-y-3 p-3 bg-red-500/10 rounded-lg border border-red-400/30">
      {/* Header — click to expand/collapse */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left"
        data-testid="record-session-toggle"
      >
        <span className="text-white text-sm font-medium flex items-center gap-2">
          🎙️ Record Session
          {!isPremium && <Crown className="w-4 h-4 text-yellow-400" />}
        </span>
        <span className="flex items-center gap-2">
          {isRecording && (
            <span className="text-red-400 text-xs animate-pulse flex items-center gap-1">
              ● REC {formattedDuration}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      <div className={open ? 'space-y-3' : 'hidden'}>

      {/* Filename + Format on one row */}
      <div className="flex gap-2">
        <div className="flex-1 space-y-1">
          <label className="text-white/70 text-xs">Filename</label>
          <Input
            type="text"
            value={filename}
            onChange={(e) => onFilenameChange(e.target.value)}
            placeholder="my-session"
            className="bg-white/10 border-white/20 text-white text-sm h-8"
            disabled={isRecording}
            data-testid="recording-filename-input"
          />
        </div>
        <div className="space-y-1">
          <label className="text-white/70 text-xs">Format</label>
          <div className="flex bg-white/5 border border-white/15 rounded-md overflow-hidden h-8" role="group" aria-label="Export format">
            {['mp3', 'webm'].map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => onExportFormatChange?.(fmt)}
                disabled={isRecording}
                className={`px-3 text-xs font-medium uppercase tracking-wider transition-colors ${
                  exportFormat === fmt
                    ? 'bg-purple-600 text-white'
                    : 'text-white/50 hover:bg-white/10'
                } ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`export-format-${fmt}`}
              >
                {fmt}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-2">
        {!isRecording ? (
          <Button
            onClick={onStartRecording}
            disabled={!isPlaying || !isPremium}
            className="flex-1 bg-red-600 hover:bg-red-700 text-sm h-8"
          >
            {!isPremium ? (
              <>
                <Crown className="w-3 h-3 mr-1" />
                Premium
              </>
            ) : (
              '⏺ Start Recording'
            )}
          </Button>
        ) : (
          <>
            <Button
              onClick={onStopRecording}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-sm h-8"
            >
              ⏹ Stop
            </Button>
            <Button
              onClick={onDownload}
              className="flex-1 bg-green-600 hover:bg-green-700 text-sm h-8"
            >
              💾 Save
            </Button>
          </>
        )}
      </div>

      {/* Help Text */}
      <div className="text-white/50 text-xs">
        {isPremium
          ? exportFormat === 'mp3'
            ? 'Record session → encode to MP3 in your browser (universally compatible)'
            : 'Record session as WebM (smaller file, faster save, may not play in older apps)'
          : 'Upgrade to Premium to record sessions'
        }
      </div>
      </div>
    </div>
  );
});

RecordingControls.displayName = 'RecordingControls';

export default RecordingControls;
